import { useState, useCallback, useRef, useEffect } from "react";

interface CardData {
  name: string;
  cvv: string;
  month: string;
  year: string;
}

interface FormErrors {
  name: string;
  cvv: string;
  month: string;
  year: string;
  phone: string;
}

interface UsePaymentFormProps {
  initialName?: string;
  initialPhone?: string;
}

export function usePaymentForm({
  initialName = "",
  initialPhone = "",
}: UsePaymentFormProps = {}) {
  const [token, setToken] = useState<string | null>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState("Credit Card");
  const [phone, setPhone] = useState(initialPhone);

  const [cardData, setCardData] = useState<CardData>({
    name: initialName,
    cvv: "",
    month: "",
    year: "",
  });

  const [errors, setErrors] = useState<FormErrors>({
    name: "",
    cvv: "",
    month: "",
    year: "",
    phone: "",
  });

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const tokenInputRef = useRef<HTMLInputElement>(null);

  // Token message listener
  useEffect(() => {
    const handleTokenMessage = (event: MessageEvent) => {
      try {
        const tokenData = JSON.parse(event.data);
        if (tokenData.message) {
          if (tokenInputRef.current) {
            tokenInputRef.current.value = tokenData.message;
          }
          setToken(tokenData.message);
        }
      } catch (error) {
        console.error("Error parsing token message:", error);
      }
    };

    window.addEventListener("message", handleTokenMessage, false);
    return () => {
      window.removeEventListener("message", handleTokenMessage, false);
    };
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;

      if (name === "phone") {
        const cleanedValue = value.replace(/\D/g, "");
        let formattedValue = cleanedValue;
        if (cleanedValue.length > 0) {
          formattedValue = `(${cleanedValue.slice(0, 3)}`;
          if (cleanedValue.length > 3) {
            formattedValue += `) ${cleanedValue.slice(3, 6)}`;
          }
          if (cleanedValue.length > 6) {
            formattedValue += `-${cleanedValue.slice(6, 10)}`;
          }
        }

        setPhone(cleanedValue);
        let error = "";
        if (cleanedValue.length === 0) {
          error = "Phone number is required";
        } else if (cleanedValue.length !== 10) {
          error = "Phone number must be 10 digits";
        }
        setErrors((prevErrors) => ({
          ...prevErrors,
          phone: error,
        }));

        e.target.value = formattedValue;
      } else {
        setCardData((prevData) => ({
          ...prevData,
          [name]: value,
        }));

        let error = "";
        switch (name) {
          case "name":
            error = value.trim() === "" ? "Cardholder name is required" : "";
            break;
          case "cvv":
            error = !/^[0-9]{3,4}$/.test(value)
              ? "CVV must be 3 or 4 digits"
              : "";
            break;
          case "month":
            error = !/^(0[1-9]|1[0-2])$/.test(value) ? "Invalid month" : "";
            break;
          case "year":
            error = !/^\d{4}$/.test(value) ? "Invalid year" : "";
            break;
          case "postal":
            error = value.trim() === "" ? "Postal code is required" : "";
            break;
          default:
            error = value.trim() === "" ? "This field is required" : "";
            break;
        }
        setErrors((prevErrors) => ({
          ...prevErrors,
          [name]: error,
        }));
      }
    },
    [],
  );

  const validateForm = useCallback(() => {
    const newErrors = {
      name: cardData.name.trim() === "" ? "Cardholder name is required" : "",
      phone:
        phone.trim() === ""
          ? "Phone number is required"
          : !/^\d{10}$/.test(phone.replace(/\D/g, ""))
            ? "Phone number must be 10 digits"
            : "",
      cvv: !/^[0-9]{3,4}$/.test(cardData.cvv)
        ? "CVV must be 3 or 4 digits"
        : "",
      month: !/^(0[1-9]|1[0-2])$/.test(cardData.month) ? "Invalid month" : "",
      year: !/^\d{4}$/.test(cardData.year) ? "Invalid year" : "",
    };
    setErrors(newErrors);
    return Object.values(newErrors).every((error) => error === "");
  }, [cardData, phone]);

  const updateCardName = useCallback((name: string) => {
    setCardData((prev) => ({ ...prev, name }));
  }, []);

  const updatePhone = useCallback((newPhone: string) => {
    const cleaned = newPhone.replace(/^\+1/, "").trim();
    setPhone(cleaned);
  }, []);

  const resetPaymentMethod = useCallback(() => {
    setSelectedPaymentMethod("");
  }, []);

  return {
    token,
    setToken,
    selectedPaymentMethod,
    setSelectedPaymentMethod,
    phone,
    setPhone: updatePhone,
    cardData,
    setCardData,
    errors,
    iframeRef,
    tokenInputRef,
    handleInputChange,
    validateForm,
    updateCardName,
    resetPaymentMethod,
  };
}
